import { yupResolver } from "@hookform/resolvers/yup";
import React, { createRef, forwardRef, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  View,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
  Platform,
  Dimensions,
} from "react-native";
import {
  BottomSheet,
  theme,
  Text,
  typography,
  Button,
  Modal as ModalComponent,
  TextInput,
  ITextInput,
  Dropdown,
  ErrorMessage,
  ErrorWarningBottomSheet,
  Icon,
} from "ui-components";
import { CreditCardFields } from "../../../enums";
import { translate } from "../../../i18n/translate";
import { verifyBillingAddressSchema } from "../../../screens/new/utils/validation-schemas-payment";
import { DomusoApiService } from "../../../api/DomusoApiService";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { getErrorMessageId, ERROR_CODES } from "../../../utils/errorCodes";
import automationTestingLocators from "../../../screens/new/utils/automation-testing-locators.json";
import { useNavigation } from "@react-navigation/native";
import { HomeParamsList } from "../../../navigation/home-stack/home-stack";
const { StateAbbrevations } = require("domuso-constants").states;

const webMaxWidth = 1200;
const mobileMaxWidth = 576;

const { STREET_ADDRESS, STREET_ADDRESS_2, CITY, STATE, ZIP } = CreditCardFields;

interface VerifyBillingAddressNewProps extends HomeParamsList {
  toggleForCardBottomSheet: Boolean;
}

export const VerifyBillingAddressNew = forwardRef(
  ({ toggleForCardBottomSheet, ...props }: VerifyBillingAddressNewProps, ref: any) => {
    const navigation = useNavigation();
    const dispatch = useDispatch();
    const errorPopup = useRef();
    const { width: screenWidth } = useWindowDimensions();
    const { height: screenHeight } = Dimensions.get("window");
    const [currentInputIndex, setCurrentInputIndex] = useState(0);
    const [selectedState, setSelectedStateValue] = useState();
    const [loading, setLoading] = useState(false);
    const [errorMsgPopup, setErrorMsgPopup] = useState<any>(false);

    const cognitoUser = useSelector((state) => state.auth.cognitoUser);
    const service = new DomusoApiService(cognitoUser, dispatch);
    const logger = useSelector((state: RootState) => state.logger);
    const inputAccessoryViewID = "inputAccessoryViewID4";
    const textInputRef = [createRef(), createRef(), createRef(), createRef(), createRef()];

    let CustomTag = screenWidth <= mobileMaxWidth ? BottomSheet : ModalComponent;
    const styles = StyleSheet.create({
      label: { fontFamily: "Lato_700Bold", color: theme.colors.darkerGrey },
    });

    const {
      register,
      trigger,
      handleSubmit,
      getValues,
      setValue: originalSetValue,
      clearErrors,
      reset: resetOriginal,
      formState: { errors },
    } = useForm({
      resolver: yupResolver(verifyBillingAddressSchema),
    });

    useEffect(() => {
      register(STREET_ADDRESS);
      register(STREET_ADDRESS_2);
      register(ZIP);
      register(CITY);
      register(STATE);
    }, [register]);

    const reset = () => {
      setSelectedStateValue(undefined);
      resetOriginal();
    };
    useEffect(() => {
      /*
        reset form values every time bottom-sheet opens 
      */
      setLoading(false);
      reset();
    }, [toggleForCardBottomSheet]);

    const setValue = (formName: string, val: any) => {
      clearErrors(formName);
      originalSetValue(formName, val);
    };
    // Should this be in redux?
    const onSubmit = async (data: any) => {
      setLoading(true);
      try {
        setErrorMsgPopup(null);
        const { streetAddress, streetAddress2, city, state, zip } = data;
        const { paymentSourceId, currentProperty } = props;

        const avsResponse = await service.verifyBillingAddress({
          property_id: currentProperty.id,
          payment_source_id: paymentSourceId,
          street_address: streetAddress,
          address_line2: streetAddress2,
          city,
          state,
          zip,
        });
        ref?.current?.close();
        setLoading(false);
        reset();

        if (props?.fromAutopay) {
          return navigation.navigate("PaymentAutoPayReviewPayment", {
            ...props,
          });
        } else {
          return navigation.navigate("PaymentFlowPaymentSummary", {
            ...props,
          });
        }
        // dispatch(setDefaultPaymentSourceUpdated(paymentSourceId));
      } catch (e) {
        reset();
        setLoading(false);
        await logger.error({ e });
        await logger.error(e);
        const { code } = e;
        const messageId = getErrorMessageId(e);

        if (
          [
            ERROR_CODES.CREDIT_CARD_INVALID_BILLING_ADDRESS_ERROR,
            ERROR_CODES.CREDIT_CARD_INVALID_BILLING_ZIP_ERROR,
          ].includes(code) ||
          !messageId
        ) {
          setErrorMsgPopup(translate("addPaymentCCDebit.avs.generic"));
          errorPopup?.current?.open();
        } else {
          setErrorMsgPopup(translate(messageId));
          errorPopup?.current?.open();
        }
        setLoading(false);
      }
    };

    return (
      <CustomTag
        ref={ref}
        height={585}
        showCloseIcon={true}
        containerStyle={{
          width:
            screenWidth <= mobileMaxWidth
              ? "100%"
              : screenWidth >= mobileMaxWidth && screenWidth <= webMaxWidth
              ? 400
              : 500,
        }}
        {...automationTestingLocators.VerifyBillingAddressNew.popup}>
        <View
          style={screenWidth <= mobileMaxWidth ? { flex: 1 } : { maxHeight: screenHeight - 100 }}>
          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1 }}>
            <Text
              style={[
                typography.title1,
                { fontFamily: "Lato_900Black", color: theme.colors.secondary },
              ]}
              {...automationTestingLocators.VerifyBillingAddressNew.heading}>
              {translate("addPaymentCCDebit.title")}
            </Text>
            <View style={{ margin: 2 }} />
            <Icon name={"credit-card-group"} height={24} width={104} />
            <>
              <View style={{ margin: 6 }} />
              <LongRow
                styles={styles}
                errors={errors}
                setValue={setValue}
                maxLength={35}
                formName={STREET_ADDRESS}
                returnKeyType="next"
                inputAccessoryViewID={inputAccessoryViewID}
                ref={textInputRef[0] as any}
                onFocus={() => setCurrentInputIndex(0)}
                onSubmitEditing={() => (textInputRef[1].current as any).focus()}
                placeholder="e.g: 123 Main Street"
                {...automationTestingLocators.VerifyBillingAddressNew.streetAddress}
              />
              <View style={{ margin: 6 }} />

              <LongRow
                styles={styles}
                errors={errors}
                setValue={setValue}
                formName={STREET_ADDRESS_2}
                returnKeyType="next"
                inputAccessoryViewID={inputAccessoryViewID}
                ref={textInputRef[1] as any}
                onFocus={() => setCurrentInputIndex(1)}
                onSubmitEditing={() => (textInputRef[2].current as any).focus()}
                placeholder="e.g: APT 124"
                {...automationTestingLocators.VerifyBillingAddressNew.streetAddress2}
              />
              <View style={{ margin: 6 }} />

              <LongRow
                styles={styles}
                setValue={setValue}
                errors={errors}
                formName={CITY}
                inputAccessoryViewID={inputAccessoryViewID}
                ref={textInputRef[2] as any}
                onFocus={() => setCurrentInputIndex(2)}
                // onSubmitEditing={() => (ref[3].current as any).focus()}
                placeholder="Enter City Name"
                {...automationTestingLocators.VerifyBillingAddressNew.city}
              />
              <View style={{ margin: 6 }} />

              <View style={{ flexDirection: "row" }}>
                <View style={{ flex: 1, marginRight: 4 }}>
                  <Dropdown
                    textInputProps={{
                      labelStyle: { fontFamily: "Lato_700Bold" },
                      error: errors?.[STATE],
                    }}
                    placeholder={`${translate("addPaymentCCDebit.stateDropdown")}`}
                    label="State"
                    pickerItem={StateAbbrevations.map((value) => ({ value, label: value }))}
                    onValueChange={(value, index) => {
                      setSelectedStateValue(value);
                      setValue(STATE, value);
                    }}
                    selectedValue={selectedState}
                    labelStyle={styles.label}
                    {...automationTestingLocators.VerifyBillingAddressNew.state}
                  />
                  {Platform.OS !== "ios" && (
                    <>
                      <View style={{ marginLeft: -8 }}>
                        <ErrorMessage
                          errors={errors}
                          style={{ fontFamily: "Lato_700Bold" }}
                          formName={STATE}
                        />
                      </View>
                    </>
                  )}
                </View>
                <View style={{ flex: 1, marginLeft: 4 }}>
                  <LongRow
                    value={getValues(ZIP)}
                    keyboardType="number-pad"
                    errors={errors}
                    styles={styles}
                    setValue={setValue}
                    formName={ZIP}
                    returnKeyType="go"
                    inputAccessoryViewID={inputAccessoryViewID}
                    ref={ref[4] as any}
                    onFocus={() => setCurrentInputIndex(4)}
                    onSubmitEditing={() => handleSubmit(onSubmit)()}
                    placeholder="12345"
                    {...automationTestingLocators.VerifyBillingAddressNew.zip}
                  />
                </View>
              </View>
            </>

            <View style={{ margin: 10, flex: 1 }} />
            <Button
              text={translate("addPaymentCCDebit.saveAndContinue") as string}
              mode="contained"
              onPress={() => {
                handleSubmit(onSubmit)();
              }}
              textStyle={{ fontFamily: "Lato_700Bold" }}
              loading={loading}
              disabled={loading}
              {...automationTestingLocators.VerifyBillingAddressNew.continueButton}
            />
            <View style={{ margin: 10 }} />
          </ScrollView>
        </View>
        <ErrorWarningBottomSheet
          ref={errorPopup}
          title={translate("addPaymentCCDebit.error") as string}
          body={errorMsgPopup}
          height={170}
          alertType={"error"}
          showButtons={false}
          {...automationTestingLocators.VerifyBillingAddressNew.errorWarningAlert}
        />
      </CustomTag>
    );
  }
);

interface LongRowProps extends ITextInput {
  styles: any;
  setValue: any;
  formName: string;
  errors?: Record<string, any>;
}
const LongRow = forwardRef(
  (
    {
      styles,
      setValue,
      formName,
      placeholder,
      errors,
      render,
      maxLength,
      keyboardType = "default",
      returnKeyType,
      inputAccessoryViewID,
      onFocus,
      onSubmitEditing,
      value,
      ...props
    }: LongRowProps,
    ref: any
  ) => {
    return (
      <View accessible={true}>
        <TextInput
          value={value}
          labelStyle={{ fontFamily: "Lato_700Bold" }}
          label={translate(`addPaymentCCDebit.${formName}`) as string}
          error={errors?.[formName]}
          mode="outlined"
          render={render}
          keyboardType={keyboardType}
          maxLength={maxLength}
          placeholder={placeholder}
          onChangeText={(text) => setValue(formName, text)}
          ref={ref}
          returnKeyType={returnKeyType}
          inputAccessoryViewID={inputAccessoryViewID}
          onFocus={onFocus}
          onSubmitEditing={onSubmitEditing}
          {...props}
        />
      </View>
    );
  }
);
